import { useEffect, useMemo, useState } from 'react';
import type { ReplaySummary } from '@ccc/shared-types';
import { CARD_CATALOG } from '@ccc/cards';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { apiJson, loadReplays } from '../lib/multiplayer';
import { cardById } from '../lib/localGame';
import { useAuthStore } from '../lib/auth';

export function ReplaysRoute() {
  const [sort, setSort] = useState<'recent' | 'top'>('recent');
  const [card, setCard] = useState('');
  const [replays, setReplays] = useState<ReplaySummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isOwner = user?.canAdmin && user.username === 'FM';

  const query = useMemo(() => {
    const params = new URLSearchParams({ sort });
    if (card) params.set('card', card);
    return `?${params.toString()}`;
  }, [card, sort]);

  useEffect(() => {
    let alive = true;
    const loader = showHidden && isOwner
      ? apiJson<{ replays: ReplaySummary[] }>('/api/owner/replays/hidden')
      : loadReplays(query);
    loader
      .then((data) => {
        if (alive) {
          setReplays(data.replays);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : 'Could not load replays.');
      });
    return () => {
      alive = false;
    };
  }, [isOwner, query, showHidden]);

  return (
    <div className="min-h-full w-full max-w-[1200px] mx-auto px-3 md:px-8 py-4 pb-12 flex flex-col gap-4">
      <div>
        <h1 className="font-display text-3xl font-bold">Replays</h1>
        <p className="text-sm text-ink-muted">Recent games, top liked games, and games by card.</p>
      </div>
      <Panel className="p-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant={sort === 'recent' ? 'primary' : 'secondary'} onClick={() => setSort('recent')}>
          Recent
        </Button>
        <Button size="sm" variant={sort === 'top' ? 'primary' : 'secondary'} onClick={() => setSort('top')}>
          Top liked
        </Button>
        {isOwner && (
          <Button size="sm" variant={showHidden ? 'primary' : 'secondary'} onClick={() => setShowHidden((v) => !v)}>
            Hidden
          </Button>
        )}
        <select
          value={card}
          onChange={(e) => setCard(e.target.value)}
          className="h-8 min-w-[220px] rounded-md border border-ink/10 bg-surface px-3 text-sm focus-ring"
          aria-label="Filter by card"
        >
          <option value="">All cards</option>
          {CARD_CATALOG.map((c) => (
            <option key={c.id} value={c.id}>
              {c.shortTitle}
            </option>
          ))}
        </select>
      </Panel>
      <Panel className="p-4">
        {error && <div className="text-sm text-danger">{error}</div>}
        {!error && replays.length === 0 && (
          <div className="h-full grid place-items-center text-sm text-ink-muted">
            Finished public games will appear here.
          </div>
        )}
        <div className="grid gap-2">
          {replays.map((replay) => {
            const whiteCard = replay.whiteCardId ? cardById(replay.whiteCardId) : null;
            const blackCard = replay.blackCardId ? cardById(replay.blackCardId) : null;
            return (
              <a
                key={replay.id}
                href={`/replays/${replay.id}`}
                className="rounded-md border border-ink/10 p-3 hover:bg-ink/[0.04] transition focus-ring"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold">
                    {replay.whiteName} vs {replay.blackName}
                  </div>
                  <div className="text-xs text-ink-muted">
                    {replay.likeCount} likes · {replay.plyCount} plies{replay.hidden ? ' · Hidden' : ''}
                  </div>
                </div>
                <div className="mt-1 text-xs text-ink-muted">
                  {whiteCard?.shortTitle ?? 'No card'} vs {blackCard?.shortTitle ?? 'No card'} ·{' '}
                  {replay.winner === 'draw' ? 'Draw' : `${replay.winner ?? 'Game'} won`}
                </div>
              </a>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
